/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TableHead,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import DoneIcon from '@material-ui/icons/Done';
import TCell, { TableCellProps } from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableFooter from '@material-ui/core/TableFooter';
import TablePagination from '@material-ui/core/TablePagination';
import TRow, { TableRowProps } from '@material-ui/core/TableRow';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckIcon from '@material-ui/icons/CheckCircle';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import OrderIcon from '@material-ui/icons/ImportExport';
import MoreIcon from '@material-ui/icons/MoreVert';
import RemoveIcon from '@material-ui/icons/RemoveCircle';
import Alert from '@material-ui/lab/Alert';
import clsx from 'clsx';
import Loading from 'components/Loading';
import i18n from 'i18n';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import React from 'react';
import { ListDataProps } from 'store/interfaces';
import { cyrb53Hash, shortString } from 'utils/helpers';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import styles from './styles';

const useStyles = makeStyles(styles);

interface ExtendTableCellProps extends TableCellProps {
  centerTitle?: boolean;
  copyable?: boolean;
  ellipsis?: boolean;
  markOderItem?: boolean;
  maxLength?: number;
  order?: boolean;
  textAlignCenter?: boolean;
  tooltip?: string;
  editable?: boolean;
  action?: (value: string) => void;
  handleOrder?: () => void;
}

export const TableCell = (props: ExtendTableCellProps) => {
  const classes = useStyles();
  const isString = typeof props.children === 'string';
  const text = props?.tooltip ?? String(props?.children);
  const [tooltipText, setTooltipText] = React.useState(
    isString ? props.children : ''
  );
  const [editCell, setEditCell] = React.useState(false);
  const [valueEditCell, setValueEditCell] = React.useState('');

  const handleCopy = () => {
    if (props?.copyable) {
      navigator.clipboard
        .writeText(text)
        .then(() => setTooltipText(i18n.t('COPIED_KEY')));
    }
  };

  const handleEdit = () => {
    if (props?.editable) {
      setEditCell(true);
    }
  };

  const renderText = () => shortString(text, props?.maxLength || 50);
  const renderOrderIcon = () => (
    <OrderIcon
      className={clsx(classes.orderIcon, {
        [classes.orderIconMarked]: props?.markOderItem,
      })}
    />
  );

  const getCellContent = (
    props: ExtendTableCellProps,
    text: string,
    classes: ClassNameMap
  ): JSX.Element => {
    // NOSONAR
    if (typeof props?.children !== 'string') {
      return props?.children as JSX.Element; // NOSONAR
    }

    if (props?.copyable) {
      return (
        <Grid
          className={clsx(classes.cellContent, {
            [classes.alignCenterSpan]: props?.centerTitle,
          })}
        >
          <FileCopyIcon style={{ fontSize: 14, marginRight: 4 }} />
          {renderText()}
        </Grid>
      );
    }
    if (props?.editable) {
      return (
        <Grid
          className={clsx(classes.cellContent, {
            [classes.alignCenterSpan]: props?.centerTitle,
          })}
        >
          {editCell ? (
            <>
              <TextField
                id="edit-cell"
                variant="outlined"
                type="number"
                onKeyDown={evt =>
                  ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()
                }
                onChange={({ target }) => {
                  setValueEditCell(target.value);
                }}
                InputProps={{
                  inputProps: { min: '0', step: '1' },
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      onClick={() => {
                        if (props?.action) {
                          props.action(valueEditCell);
                        }
                      }}
                    >
                      {valueEditCell !== '' && (
                        <DoneIcon className={classes.doneIcon} />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </>
          ) : (
            <>
              <EditIcon className={classes.editIcon} />
              {renderText()}
            </>
          )}
        </Grid>
      );
    }

    if (props?.ellipsis) {
      return renderText();
    }

    if (props?.order) {
      return (
        <Grid
          className={clsx(classes.cellContent, {
            [classes.cellMarked]: props?.markOderItem,
            [classes.alignCenterSpan]: props?.centerTitle,
          })}
        >
          {props?.children} {renderOrderIcon()}
        </Grid>
      );
    }

    return (
      <Grid
        className={clsx(classes.cellContent, {
          [classes.alignCenterSpan]: props?.centerTitle,
        })}
      >
        {props?.children}
      </Grid>
    );
  };

  // eslint-disable-next-line consistent-return
  const propAction = () => {
    if (props?.copyable) {
      return handleCopy;
    }
    if (props.editable) {
      return handleEdit;
    }
    if (props?.handleOrder) {
      return props.handleOrder;
    }
  };

  const content = () => (
    <TCell
      className={props?.textAlignCenter ? classes.cellCenter : classes.cell}
      onClick={propAction()}
      {...props}
    >
      {props?.children || props?.children === 0
        ? getCellContent(props, text, classes)
        : '-'}
    </TCell>
  );

  return props?.tooltip || (props?.ellipsis && isString) ? (
    <Tooltip
      title={tooltipText}
      onOpen={() => setTooltipText(text)}
      placement="top"
      arrow
    >
      {content()}
    </Tooltip>
  ) : (
    content()
  );
};

export const TableRow = (props: TableRowProps) => {
  const classes = useStyles();
  return (
    <TRow
      className={clsx(classes.row, {
        [classes.pointer]: props?.onClick,
      })}
      {...props}
    />
  );
};

export const MenuActionItem = props => {
  const classes = useStyles();
  return <MenuItem {...props} className={classes.actionItem} />;
};

interface MenuActionProps {
  children: React.ReactNode;
  disabled?: boolean;
  id?: string;
}

export const MenuActions = ({ children, disabled, id }: MenuActionProps) => {
  const classes = useStyles();
  return (
    <PopupState variant="popover">
      {popupState => (
        <>
          <IconButton
            id={id}
            className={classes.moreButton}
            disabled={disabled}
            {...bindTrigger(popupState)}
          >
            <MoreIcon className={classes.moreButtonIcon} />
          </IconButton>
          <Menu
            {...bindMenu(popupState)}
            onClick={bindMenu(popupState).onClose}
          >
            {children}
          </Menu>
        </>
      )}
    </PopupState>
  );
};

interface StatusIconProps {
  id?: string;
  status: 'success' | 'blocked' | 'inactive' | 'loading';
}

export const StatusIcon = ({ id, status }: StatusIconProps) => {
  const classes = useStyles();
  const map = {
    success: <CheckIcon className={classes.iconSuccess} id={id} />,
    blocked: <CancelIcon className={classes.iconBlocked} id={id} />,
    inactive: <RemoveIcon className={classes.iconBlocked} id={id} />,
    loading: <CircularProgress size={12} id={id} />,
  };

  return map[status] || status;
};

export interface Props {
  addButton?: React.ReactNode;
  alertText?: string | null;
  colCount?: number;
  countRowLoading?: number;
  data: ListDataProps;
  fillTable?: boolean;
  header: React.ReactNode;
  isFixed?: boolean;
  isLoading: boolean;
  notFooter?: boolean;
  notFoundText: string | React.ReactNode;
  notRowsPerPage?: boolean;
  overrideRowsCount?: number;
  rowLoading?: boolean;
  smallLoading?: boolean;
  textLoading?: string;
  skeletonLoading?: boolean;
  onChangeLimit?: (limit: number) => void;
  onChangePage?: (page: number) => void;
  renderItems: (item, index) => React.ReactNode;
}

const TableFull: React.FC<Partial<Props>> = ({ children, colCount }) => {
  const classes = useStyles();

  return (
    <TableRow className={classes.withoutHover}>
      <TableCell colSpan={colCount} align="center">
        {children}
      </TableCell>
    </TableRow>
  );
};

interface TableEmptyDataProps {
  colCount: number;
  numberRows: number[];
  notFoundText: string | React.ReactNode;
  rowLoading?: boolean;
  textLoading?: string;
}

type TableRowLoadingProps = Pick<
  TableEmptyDataProps,
  'numberRows' | 'colCount' | 'textLoading'
>;

const TableRowLoading = ({
  colCount,
  numberRows,
  textLoading,
}: TableRowLoadingProps): JSX.Element => {
  const classes = useStyles(styles);
  return (
    <>
      {numberRows?.map(item => (
        <TableRow key={item} className={classes.withoutHover}>
          <TableCell colSpan={colCount} align="center">
            <Grid container alignItems="center" justify="center">
              <CircularProgress
                className={classes.rowLoadingCircular}
                size={16}
                color="secondary"
              />
              {!!textLoading && <Typography> {textLoading} </Typography>}
            </Grid>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};

const TableEmptyData = ({
  colCount,
  numberRows,
  notFoundText,
  rowLoading,
  textLoading,
}: TableEmptyDataProps): JSX.Element => {
  return (
    <>
      {rowLoading ? (
        <TableRowLoading
          numberRows={numberRows}
          colCount={colCount}
          textLoading={textLoading}
        />
      ) : (
        <TableFull colCount={colCount}>
          <Typography style={{ fontSize: 13 }}>{notFoundText}</Typography>
        </TableFull>
      )}
    </>
  );
};

export default function CustomTable({
  addButton,
  alertText,
  colCount = 12,
  countRowLoading,
  data,
  fillTable,
  header,
  isFixed,
  isLoading,
  notFooter,
  notFoundText,
  notRowsPerPage,
  overrideRowsCount,
  rowLoading,
  smallLoading,
  textLoading,
  skeletonLoading,
  onChangeLimit,
  onChangePage,
  renderItems,
}: Props) {
  const classes = useStyles();

  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(
    overrideRowsCount || 25
  );
  const [numberRows, setNumberRows] = React.useState([0]);

  React.useEffect(() => {
    if (!!overrideRowsCount && overrideRowsCount !== rowsPerPage) {
      setRowsPerPage(overrideRowsCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideRowsCount]);

  React.useEffect(() => {
    setNumberRows(Array.from(Array(countRowLoading).keys()));
  }, [countRowLoading]);

  return (
    <>
      <TableContainer
        className={clsx({
          [classes.isFixed]: isFixed,
        })}
        component={Paper}
        data-testid={'autosky-tableitems'}
      >
        <Table
          className={clsx({
            [classes.isFixed]: isFixed,
          })}
        >
          <TableHead>
            <TableRow className={classes.withoutHover}>{header}</TableRow>
          </TableHead>
          <TableBody>
            {isLoading && !skeletonLoading && (
              <TableFull colCount={colCount}>
                {isFixed ? (
                  <Loading smallLoading={smallLoading} size={40} />
                ) : (
                  <Loading smallLoading={smallLoading} size={20} />
                )}
              </TableFull>
            )}
            {isLoading && skeletonLoading && (
              <>
                {(data?.results?.length ? data.results : [null]).map(
                  (row, index) => (
                    <TableRow
                      key={row ? cyrb53Hash(String(index)) : 'empty-row'}
                      className={classes.withoutHover}
                    >
                      <TableCell colSpan={colCount} align="center">
                        <Grid container alignItems="center" justify="center">
                          <Typography
                            variant="h4"
                            component="div"
                            className={classes.loadingText}
                          >
                            {}
                          </Typography>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </>
            )}

            {!isLoading && alertText && (
              <TableFull colCount={colCount}>
                <Alert
                  className={classes.alert}
                  severity="warning"
                  color="info"
                >
                  <Grid item>
                    <Typography>{alertText}</Typography>
                  </Grid>
                </Alert>
              </TableFull>
            )}
            {!isLoading && !alertText && !data?.results?.length && (
              <TableEmptyData
                numberRows={numberRows}
                notFoundText={notFoundText}
                colCount={colCount}
                textLoading={textLoading}
                rowLoading={rowLoading}
              />
            )}
            {!isLoading && !alertText && !!data?.results?.length && (
              <>
                {rowLoading && (
                  <TableRowLoading
                    numberRows={numberRows}
                    colCount={colCount}
                    textLoading={textLoading}
                  />
                )}
                {data?.results?.map((row, index) => {
                  if (row) {
                    return renderItems(row, index);
                  }

                  return (
                    <TableRow
                      key={cyrb53Hash(String(index))}
                      className={classes.withoutHover}
                    >
                      <TableCell colSpan={colCount} align="center">
                        <Grid container alignItems="center" justify="center">
                          <Typography>{notFoundText}</Typography>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!!addButton && (
                  <TableRow className={classes.withoutHover}>
                    <TableCell colSpan={colCount} align="center">
                      <Grid container alignItems="center" justify="center">
                        {addButton}
                      </Grid>
                    </TableCell>
                  </TableRow>
                )}

                {fillTable && (
                  <TableRow className={classes.fillTable}>
                    <TableCell colSpan={colCount} align="center"></TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
          {!notFooter && onChangePage && onChangeLimit && (
            <TableFooter>
              <TableRow className={classes.withoutHover}>
                <TablePagination
                  className={classes.pagination}
                  labelRowsPerPage={i18n.t('ROWS_PER_PAGE_KEY')}
                  rowsPerPageOptions={notRowsPerPage ? [] : [10, 25, 50, 100]}
                  colSpan={colCount}
                  count={data?.count}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{ native: true }}
                  onChangePage={(e, newPage) => {
                    setPage(newPage);
                    onChangePage(newPage);
                  }}
                  onChangeRowsPerPage={({ target }) => {
                    setRowsPerPage(+target.value);
                    onChangeLimit(+target.value);
                    setPage(0);
                    onChangePage(0);
                  }}
                  labelDisplayedRows={({ from, to, count }) => {
                    if (from > to) {
                      setPage(0);
                    }

                    return `${from > to ? to : from}-${
                      to === count ? to : to + 1
                    } de ${count}`;
                  }}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </>
  );
}
